import styles from "@/styles/csotd/csotdIndex.module.scss"
import { useState, useEffect } from 'react'
import DefaultLayout from "@/components/DefaultLayout"
import SliderBarLeft from "@/components/SliderBarLeft"
import SliderBarRight from "@/components/SliderBarRight"
import Countdown from '@/components/csotd/countdown'
import ProductInfo from '@/components/productDetail/ProductInfo'
import productApi from '@/service/modules/productApi'
import csotdApi from '@/service/modules/csotdApi'
import Head from "next/head"
import { getCookie, setCookie } from '@/utils/cookie'
import { useRouter } from 'next/router'
import GoodsComponentWithGrab from "@/components/csotd/GoodsComponentWithGrab"
import dayjs from 'dayjs'
import Script from "next/script"
import getConfig from 'next/config'

const { publicRuntimeConfig } = getConfig()
const inspectionModeEnabled = publicRuntimeConfig.inspectionModeEnabled === true

interface PictureItem {
  thumbnailUrl: string;
  imageHDUrl: string;
  imageORUrl: string;
  fileNameUrl: string;
  isDefault: number;
}

interface ProductItem {
  type: string,
  productID: string;
  productName: string;
  url: string;
  filename: string;
  promotion: string;
  review: number;
  reviewCount: number;
  price: number;
  promotionPrice: number;
  tagTitle: string;
  tagBgColor: string;
  tagFontColor: string;
  freeShipping: number;
  onSale: number;
  reduced: number;
  isNew: number;
  isEmailOnly: any,
  promotionTypeID: string | number;
}

interface BreadcrumbItem {
  name: string;
  url: string;
  current: boolean;
}

interface ReviewData {
  avgRank: number;
  total: number;
  summary: any;
}

interface paymentMethodItem {
  paymentName: string;
  paymentIcon: string;
}

interface variantProductsItem {
  productID: number;
  name: string;
  thumbnail: string;
  defaultMember: number;
  url: string;
  options: { optionID: number; variantID: number }[]; // 内联Option对象数组
}

interface ProductDetailData {
  name: string;
  productID: number;
  categoryID: number;
  url: string;
  enabled: number;
  discontinued: number;
  price: number;
  promotionPrice: number;
  normalPrice: number;
  promotionTypeID: string | number;
  inStock: number;
  courierID: number;
  freeShipping: number;
  preSales: number;
  preSalesDate: string;
  isDropShipping?: number;
  isEmailOnly: boolean;
  isNew: number | undefined;
  isGroup: number;
  onSale: number;
  reduced: number;
  pictures: PictureItem[];
  newThumbnail: string;
  filename: string;
  promotion: {
    productID: number;
    promotionType: number;
    promotionPrice: number;
    normalPrice: number;
    endDate: string;
    status: number;
    notified: number;
    pointRate: string;
    discountPercent: string;
    ebaySync: number;
  };
  emailOnlyFinished: number;
  breadcrumb: BreadcrumbItem[];
  review: ReviewData | null | undefined;
  paymentMethod: paymentMethodItem[];
  currentTime: string;
  warehouseID: number;
  crazyDeal?: {
    hoursLeft: number;
    minutesLeft: number;
    secondsLeft: number;
  } | undefined;
  variants: {
    options: {
      color?: any;
      size?: any
    };
    products: variantProductsItem[];
  };
  seo: {
    title: string;
    description: string;
    keywords: string;
  };
  features?: string;
}

export async function getServerSideProps() {
  try {
    let productData = await csotdApi.getCrazyDeal()
    return {
      props: {
        productData
      }
    };
  } catch (error) {
    console.log('Next.js Error :', dayjs().format('YY-MM-DD HH:mm:ss'), `/ promise all：`, JSON.stringify(error));
    return {
      notFound: true,
    }
  }
}

export default function ProductPage({ productData }: any) {
  const [productDetail, setProductDetail] = useState<ProductDetailData | null>()
  const [updateProductDetail, setUpdateProductDetail] = useState<ProductDetailData | null>()
  const [review, setReview] = useState<ReviewData>({
    avgRank: 5,
    total: 0,
    summary: {
      rating1: 0,
      rating2: 0,
      rating3: 0,
      rating4: 0,
      rating5: 0,
    },
  });
  const [paymentMethod, setPaymentMethod] = useState<paymentMethodItem[] | []>()
  const [dailySideData, setDailySideData] = useState<ProductItem[]>([]);

  // 处理商品详情数据
  async function getProductDetail(data?: any) {
    try {
      let productData = data ? data : await csotdApi.getCrazyDeal()
      if (productData.data.product) {
        // 优化图片，如果是默认图，又不在前12张缩略图里面。将其放到第一位
        if (
          productData.data.product
          && productData.data.product.pictures
        ) {
          let pictures = JSON.parse(JSON.stringify(productData.data.product.pictures))
          let needInject = false
          let target: any = []

          let firstMagnifierImage = ''

          pictures.forEach((item: any, index: number) => {
            if (item.isDefault === 1 && index >= 12) {
              needInject = true
              target = item
            }

            if (item.isDefault === 1) {
              firstMagnifierImage = item.imageHDUrl || item.imageORUrl || item.fileNameUrl
              productData.data.product.firstMagnifierImage = firstMagnifierImage
            }
          })
          if (!firstMagnifierImage) {
            let target = pictures[0]
            firstMagnifierImage = target.imageHDUrl || target.imageORUrl || target.fileNameUrl
          }
          if (needInject) {
            pictures.unshift(target)
            productData.data.product.pictures = pictures
          }
        }

        setPaymentMethod(productData.data.paymentMethod)
        setProductDetail(productData.data.product)
        setUpdateProductDetail(productData.data.product)

        getReviewData(productData.data.product.productID)
        getDailySideDealsData(productData.data.product.productID)
      }
    } catch (error) {
      console.log(`crazysales数据获取失败： `, error)
    }
  }

  // 获取产品详情
  async function getReviewData(id: string | number) {
    try {
      let reviewData = await productApi.getProductScreen(id)

      if (reviewData?.data) {
        setReview(reviewData.data.review)
      }
    } catch (error) {
      console.log(`reviewData数据获取失败： `, error)
    }
  }

  // 获取Daily Side商品
  async function getDailySideDealsData(id: string | number) {
    try {
      let dailySideData = await csotdApi.getDailySideDeals(id)

      if (dailySideData?.data) {
        setDailySideData(dailySideData?.data)
      }
    } catch (error) {
      console.log(`Daily Side商品获取失败： `, error)
    }
  }

  // 处理商品详情数据
  useEffect(() => {
    if (productData) {
      getProductDetail(productData)
    }
  }, [productData])

  // 切换商品数量
  const [quantityNum, setQuantityNum] = useState(1)
  const handleQuantityChange = (value: number) => {
    console.log(`selected ${value}`)
    setQuantityNum(value)
  }
  let quantityOptions = [...new Array((productDetail?.inStock && productDetail?.inStock > 100) ? 100 : productDetail?.inStock)].map((v: any, i: number) => {
    const stock = i + 1
    let label = `${stock}`
    if (productDetail && productDetail?.inStock > 100 && stock === 100) {
      label = `100(Max)`
    }
    return { value: stock, label: label }
  })

  const router = useRouter()
  // 判断是否有aid 或 recoveraid在url，有就将其
  useEffect(() => {
    const { aid, recoveraid }: any = router.query
    let lastAidValue = ''
    let lastRecoveraidValue = ''

    // 检查是否有"aid"参数，如有多个取最后
    if (Array.isArray(aid) && aid.length > 0) {
      lastAidValue = aid[aid.length - 1]
    } else if (typeof aid === 'string') {
      // 如果只有一个"aid"参数，它将是一个字符串
      lastAidValue = aid
    }

    // 检查是否有"recoveraid"参数，如有多个取最后
    if (Array.isArray(recoveraid) && recoveraid.length > 0) {
      lastRecoveraidValue = recoveraid[recoveraid.length - 1]
    } else if (typeof recoveraid === 'string') {
      // 如果只有一个"recoveraid"参数，它将是一个字符串
      lastRecoveraidValue = recoveraid
    }

    // 优先于异步请求，将aid存入cookie, 60秒过期（后续会发起请求重新设置）
    if (lastAidValue || lastRecoveraidValue) {
      let currentDomain = window.location.hostname;
      if (currentDomain == 'www.crazysales.com.au') {
        currentDomain = ".crazysales.com.au"
      }
      setCookie('affiliateid', lastAidValue || lastRecoveraidValue, { expires: 30, secure: true, domain: `${currentDomain}`, })
    }
  }, [router.query])

  // 发现本地有存储aid，重新请求数据
  const getProductDetailWithPromotion = async () => {
    if (!getCookie('affiliateid')) return

    getProductDetail()
  }

  // cookie有留存aid参数，则更新Email价格
  useEffect(() => {
    setTimeout(() => {
      let affiliateid = getCookie('affiliateid')
      if (affiliateid) getProductDetailWithPromotion()
    }, 400)
  }, [])

  const { data: { product } } = productData
  const productInfo = {
    name: product.name,
    description: product.shortDescription,
    image: product.filename,
    sku: product.productID,
    price: product.price,
    currency: 'AUD',
    availability: 'https://schema.org/InStock',
    url: `https://www.bestdeals.co.nz${product.url}`,
  };

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": productInfo.name,
    "image": [productInfo.image],
    "description": productInfo.description,
    "sku": productInfo.sku,
    "offers": {
      "@type": "Offer",
      "url": productInfo.url,
      "priceCurrency": productInfo.currency,
      "price": productInfo.price,
      "availability": productInfo.availability,
      "itemCondition": "https://schema.org/NewCondition"
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Producy",
    "name": "BestDeals",
    "url": "https://bestdeals.co.nz/",
    "logo": "https://img.bestdeals.co.nz/images/next/images/logo.png"
  };

  return (
    <>
      <Head>
        <title>Best Deal of the Day | Exclusive Daily Discounts | BestDeals.co.nz</title>
        <meta name="keywords" content="best deals nz, bestdeals nz, appliances deals, best deals new zealand"></meta>
        <meta name="description" content="Don't miss the Best Deal of the Day at Bestdeals.co.nz! Discover handpicked products at unbeatable prices, updated daily. Limited availability – grab the offer now."></meta>
      </Head>

      <DefaultLayout >

        {/* 详情页真实代码 */}
        <div className={`${styles['page-product-detail']} slider-wapper`}>
          <SliderBarLeft></SliderBarLeft>
          <SliderBarRight></SliderBarRight>

          {/* PromotionBox */}
          {
            !inspectionModeEnabled
            && (updateProductDetail?.promotion && updateProductDetail.promotion?.promotionType)
            &&
            <Countdown
              currentTime={updateProductDetail.currentTime}
              promotion={updateProductDetail.promotion}
              crazyDeal={updateProductDetail.crazyDeal}
            ></Countdown>
          }

          {/* 商品介绍 */}
          {
            !!(updateProductDetail && updateProductDetail.productID)
            &&
            <ProductInfo
              productDetailData={updateProductDetail}
              review={review}
              paymentMethod={paymentMethod}
              quantityOptions={quantityOptions}
              quantityNum={quantityNum}
              displayPurposesOnly={true}
              handleQuantityChange={handleQuantityChange}
            />
          }

          {/* Daily Side Deals Below! */}
          {
            !!(dailySideData && dailySideData.length)
            &&
            <div className={styles['daily-side-box']}>
              <div className={styles['daily-title']}>
                Daily Side Deals Below!
              </div>

              <div className={styles['daily-item-box']}>
                {
                  dailySideData.map((item: any, index: number) => (
                    <div className={styles['daily-item']} key={index}>
                      <GoodsComponentWithGrab {...{ ...item, productName: item.name, imgUrl: item.filename }} type='Recommend' />
                    </div>
                  ))
                }
              </div>
            </div>
          }


          {/* PromotionBox */}
          {
            !inspectionModeEnabled
            && (updateProductDetail?.promotion && updateProductDetail.promotion?.promotionType)
            &&
            <Countdown
              currentTime={updateProductDetail.currentTime}
              promotion={updateProductDetail.promotion}
              crazyDeal={updateProductDetail.crazyDeal}
            ></Countdown>
          }
        </div>
      </DefaultLayout>

      <Script
        type="application/ld+json"
        id="product-schema"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
      />
      <Script
        type="application/ld+json"
        id="org-schema"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
    </>
  )
}
